import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BodyweightSelection.css';

const BodyweightSelection = () => {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState('');

    // Bodyweight workout presets (from Android code)
    const bodyweightWorkouts = [
        { code: "~w@d^_", name: "leg chest back core arm shoulder workout" },
        { code: "_^d@", name: "shoulder arm core back workout" },
        { code: "@_@_", name: "back shoulder back shoulder workout" },
        { code: "wd^^dw", name: "chest core arm arm core chest workout" },
        { code: "^d^^", name: "arm core arm arm workout" },
        { code: "wd_wd", name: "chest core shoulder chest core workout" },
        { code: "~w@d^_", name: "leg chest back core arm shoulder workout" },
        { code: "d_^dd@", name: "core shoulder arm core core back workout" },
        { code: "@d_@d_", name: "back core shoulder back core shoulder workout" },
        { code: "wdw^@^wdw", name: "chest core chest arm back arm chest core chest workout" },
        { code: "^_d^_^", name: "arm shoulder core arm shoulder arm workout" },
        { code: "w_w_@", name: "chest shoulder chest shoulder back workout" }
    ];

    // Muscle groups mapping (from Android app)
    const muscleGroups = [
        { name: "legbody", code: "~" },
        { name: "chestbody", code: "w" },
        { name: "backbody", code: "@" },
        { name: "corebody", code: "d" },
        { name: "armbody", code: "^" },
        { name: "shoulderbody", code: "_" }
    ];

    const allowedChars = "~w@d^_";

    // Handle adding character to input
    const addCharacter = (char) => {
        const newInput = userInput + char;
        const lastThreeChars = newInput.slice(-3);

        // Prevent three consecutive same characters
        if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
            setUserInput(newInput);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const input = e.target.value.toLowerCase();
        const validInput = input.split('').filter(char => allowedChars.includes(char)).join('');

        // Check for three consecutive same characters
        let result = '';
        for (let i = 0; i < validInput.length; i++) {
            const char = validInput[i];
            const lastThreeChars = result.slice(-2) + char;
            if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
                result += char;
            }
        }

        setUserInput(result);
    };

    // Handle preset workout selection
    const handlePresetSelect = (workoutCode) => {
        setUserInput(workoutCode);
    };

    // Handle next button with URL encoding fix
    const handleNext = () => {
        if (userInput.trim() !== '') {
            // Navigate with properly encoded workout code
            const encodedWorkoutCode = encodeURIComponent(userInput);
            navigate(`/bodyweight-rep-count/${encodedWorkoutCode}`, {
                state: {
                    workoutCode: userInput, // Pass unencoded version in state
                    workoutType: 'bodyweight'
                }
            });
        }
    };

    // Generate random background image
    const getRandomBackground = () => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    };

    return (
        <div
            className="bodyweight-selection-container"
            style={{
                backgroundImage: getRandomBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Dark overlay */}
            <div className="bodyweight-overlay"></div>

            {/* Fixed top bar */}
            <div className="bodyweight-top-bar">
                <button
                    className="bodyweight-back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back
                </button>

                <input
                    type="text"
                    className="bodyweight-input"
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Enter bodyweight code..."
                />

                <button
                    className="bodyweight-next-button"
                    onClick={handleNext}
                    disabled={userInput.trim() === ''}
                >
                    Next 💪
                </button>
            </div>

            {/* Scrollable content */}
            <div className="bodyweight-content">
                <div className="bodyweight-header">
                    <h1>💪 BODYWEIGHT MODE 💪</h1>
                    <p>Build strength with your own body weight!</p>
                </div>

                {/* Muscle group buttons */}
                <div className="muscle-groups-grid">
                    {muscleGroups.map((group) => (
                        <button
                            key={group.code}
                            className="muscle-group-button"
                            onClick={() => addCharacter(group.code)}
                        >
                            <span className="muscle-code">{group.code}</span>
                            <span className="muscle-name">{group.name}</span>
                        </button>
                    ))}
                </div>

                {/* Character selector */}
                <div className="character-selector">
                    <h3>Quick Character Input:</h3>
                    <div className="character-grid">
                        {allowedChars.split('').map((char) => (
                            <button
                                key={char}
                                className="character-button"
                                onClick={() => addCharacter(char)}
                            >
                                {char}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preset bodyweight workouts */}
                <div className="preset-workouts">
                    <h3>🏋️ Pure Bodyweight Presets 🏋️</h3>
                    <div className="preset-workouts-grid">
                        {bodyweightWorkouts.map((workout, index) => (
                            <div
                                key={`${workout.code}-${index}`}
                                className="preset-workout-card"
                                onClick={() => handlePresetSelect(workout.code)}
                            >
                                <div className="preset-code">{workout.code}</div>
                                <div className="preset-name">{workout.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bodyweight-instructions">
                    <p>💪 No equipment needed - just your body!</p>
                    <p>🏋️ Build functional strength and endurance</p>
                    <p>🎯 Perfect for anywhere, anytime workouts!</p>
                </div>
            </div>
        </div>
    );
};

export default BodyweightSelection;